import { z } from 'zod';
/**
 * The declarative, composable section/entity-type system (plan §3).
 *
 * - Layer A — {@link fieldDescriptorSchema}: what data an entity holds.
 * - Layer B — {@link kindDefSchema}: an entity type = fields + chosen views + link config.
 * - Layer C — `Section` (see adventure.ts): the placement of a kind into a binder.
 *
 * Built-in (prefab) kinds ship here in `BUILTIN_KINDS`; custom kinds are authored
 * per-adventure in the manifest `kinds:` block with the exact same shape. Both resolve
 * through {@link resolveKinds} into one lookup, so there is a single rendering/storage path.
 */
// ---------------------------------------------------------------------------
// Layer A — Field descriptors
// ---------------------------------------------------------------------------
export const fieldTypeSchema = z.enum([
    'text',
    'markdown',
    'number',
    'boolean',
    'select',
    'tags',
    'image',
    'ref',
    'refList',
    'refCount',
    'statblock',
    'rows',
]);
/** Hints that let generic views render any kind without bespoke code. */
export const fieldRoleSchema = z.enum(['title', 'subtitle', 'icon', 'image', 'body']);
export const fieldDescriptorSchema = z
    .object({
    key: z.string(),
    type: fieldTypeSchema,
    label: z.string(),
    required: z.boolean().optional(),
    role: fieldRoleSchema.optional(),
    /** For `select`. */
    options: z.array(z.string()).optional(),
    /** For `ref`/`refList`: the target kind id(s). */
    target: z.union([z.string(), z.array(z.string())]).optional(),
    help: z.string().optional(),
})
    .catchall(z.unknown());
// ---------------------------------------------------------------------------
// View registry ids (resolved to components in web/)
// ---------------------------------------------------------------------------
/** List visualizations. */
export const listViewSchema = z.enum([
    'cards',
    'grid',
    'table',
    'list',
    'gallery',
    'journal',
    'mapGrouped',
    'statblockCards',
    'page', // singleton markdown (overview, page, notes)
    'initiative', // runtime tool (no authored entities)
    'none',
]);
/** Detail visualizations. */
export const detailViewSchema = z.enum([
    'profile',
    'page',
    'statblock',
    'sheet',
    'encounter',
    'roomDetail',
    'none',
]);
/** Any view id (used loosely; the kind decides whether it's a list or detail slot). */
export const viewIdSchema = z.union([listViewSchema, detailViewSchema]);
export const LIST_VIEW_IDS = listViewSchema.options;
export const DETAIL_VIEW_IDS = detailViewSchema.options;
// ---------------------------------------------------------------------------
// Layer B — Kind definitions
// ---------------------------------------------------------------------------
export const kindScopeSchema = z.enum(['adventure', 'system']);
export const kindDefSchema = z
    .object({
    id: z.string(),
    label: z.string(),
    icon: z.string().default('📄'),
    scope: kindScopeSchema.default('adventure'),
    /** Cannot be removed/retyped when placed (the 6 fixed adventure kinds + system kinds). */
    fixed: z.boolean().optional(),
    /** One document, not a list (e.g. overview, page, notes). */
    singleton: z.boolean().optional(),
    fields: z.array(fieldDescriptorSchema).default([]),
    views: z
        .object({ list: listViewSchema.default('cards'), detail: detailViewSchema.default('profile') })
        .default({ list: 'cards', detail: 'profile' }),
    /** Folder name for this kind's `.md` files. */
    storageDir: z.string().default(''),
})
    .catchall(z.unknown());
/** The field key whose value maps to the Markdown body, if any. */
export function bodyFieldKey(kind) {
    return kind.fields.find((f) => f.role === 'body')?.key ?? null;
}
// ---------------------------------------------------------------------------
// Built-in prefab kinds (plan §3b)
// ---------------------------------------------------------------------------
function f(key, type, label, extra = {}) {
    return { key, type, label, ...extra };
}
/** The prefab kinds the app ships. Same shape as custom kinds. */
export const BUILTIN_KINDS = {
    // --- System kinds (locked; rendered bottom) ---
    gallery: kindDefSchema.parse({
        id: 'gallery',
        label: 'Galleria',
        icon: '🖼️',
        scope: 'system',
        fixed: true,
        storageDir: '',
        views: { list: 'gallery', detail: 'none' },
        fields: [],
    }),
    music: kindDefSchema.parse({
        id: 'music',
        label: 'Musica',
        icon: '🎵',
        scope: 'system',
        fixed: true,
        storageDir: '',
        views: { list: 'list', detail: 'none' },
        fields: [],
    }),
    notes: kindDefSchema.parse({
        id: 'notes',
        label: 'Note del DM',
        icon: '🗒️',
        scope: 'system',
        fixed: true,
        singleton: true,
        storageDir: '',
        views: { list: 'page', detail: 'none' },
        fields: [f('authorNotes', 'markdown', 'Note', { role: 'body' })],
    }),
    compendium: kindDefSchema.parse({
        id: 'compendium',
        label: 'Compendio',
        icon: '📚',
        scope: 'system',
        fixed: true,
        storageDir: '',
        views: { list: 'table', detail: 'statblock' },
        fields: [],
    }),
    // --- Adventure kinds — FIXED (locked; rendered top) ---
    place: kindDefSchema.parse({
        id: 'place',
        label: 'Luoghi',
        icon: '🗺️',
        scope: 'adventure',
        fixed: true,
        storageDir: 'places',
        views: { list: 'mapGrouped', detail: 'roomDetail' },
        fields: [
            f('name', 'text', 'Nome', { role: 'title', required: true }),
            f('icon', 'text', 'Icona', { role: 'icon' }),
            f('image', 'image', 'Immagine di riferimento', { role: 'image' }),
            f('floor', 'text', 'Piano/Area'),
            f('description', 'markdown', 'Descrizione', { role: 'body' }),
            f('readAloud', 'markdown', 'Lettura ad alta voce'),
            f('event', 'markdown', 'Evento'),
            f('details', 'tags', 'Dettagli'),
            f('secret', 'text', 'Segreto'),
            f('npcs', 'refList', 'PNG', { target: 'npc' }),
            f('items', 'refList', 'Oggetti', { target: 'item' }),
            f('clues', 'refList', 'Indizi', { target: 'clue' }),
            f('connections', 'refList', 'Collegamenti', { target: 'place' }),
        ],
    }),
    character: kindDefSchema.parse({
        id: 'character',
        label: 'Personaggi',
        icon: '🛡️',
        scope: 'adventure',
        fixed: true,
        storageDir: 'characters',
        views: { list: 'cards', detail: 'sheet' },
        fields: [
            f('name', 'text', 'Nome', { role: 'title', required: true }),
            f('race', 'text', 'Razza'),
            f('class', 'text', 'Classe'),
            f('level', 'number', 'Livello'),
            f('image', 'image', 'Ritratto', { role: 'image' }),
            f('description', 'text', 'Descrizione', { role: 'subtitle' }),
            f('backstory', 'markdown', 'Background', { role: 'body' }),
            f('statblock', 'statblock', 'Statistiche'),
        ],
    }),
    npc: kindDefSchema.parse({
        id: 'npc',
        label: 'PNG',
        icon: '🎭',
        scope: 'adventure',
        fixed: true,
        storageDir: 'npcs',
        views: { list: 'cards', detail: 'profile' },
        fields: [
            f('name', 'text', 'Nome', { role: 'title', required: true }),
            f('role', 'text', 'Ruolo', { role: 'subtitle' }),
            f('icon', 'text', 'Icona', { role: 'icon' }),
            f('image', 'image', 'Ritratto', { role: 'image' }),
            f('description', 'markdown', 'Descrizione', { role: 'body' }),
            f('notes', 'markdown', 'Note'),
        ],
    }),
    beast: kindDefSchema.parse({
        id: 'beast',
        label: 'Bestiario',
        icon: '🐉',
        scope: 'adventure',
        fixed: true,
        storageDir: 'beasts',
        views: { list: 'statblockCards', detail: 'statblock' },
        fields: [
            f('name', 'text', 'Nome', { role: 'title', required: true }),
            f('icon', 'text', 'Icona', { role: 'icon' }),
            f('image', 'image', 'Immagine di riferimento', { role: 'image' }),
            f('type', 'text', 'Tipo', { role: 'subtitle' }),
            f('cr', 'text', 'Grado Sfida'),
            f('statblock', 'statblock', 'Statistiche'),
            f('actions', 'tags', 'Azioni'),
            f('notes', 'markdown', 'Note', { role: 'body' }),
        ],
    }),
    encounter: kindDefSchema.parse({
        id: 'encounter',
        label: 'Incontri',
        icon: '⚔️',
        scope: 'adventure',
        fixed: true,
        storageDir: 'encounters',
        views: { list: 'cards', detail: 'encounter' },
        fields: [
            f('name', 'text', 'Nome', { role: 'title' }),
            f('difficulty', 'text', 'Difficoltà', { role: 'subtitle' }),
            f('image', 'image', 'Immagine di riferimento', { role: 'image' }),
            f('room', 'ref', 'Luogo', { target: 'place' }),
            f('monsters', 'refCount', 'Mostri', { target: 'beast' }),
            f('notes', 'markdown', 'Note', { role: 'body' }),
        ],
    }),
    initiative: kindDefSchema.parse({
        id: 'initiative',
        label: 'Iniziativa',
        icon: '⏱️',
        scope: 'adventure',
        fixed: true,
        storageDir: '',
        views: { list: 'initiative', detail: 'none' },
        fields: [],
    }),
    // --- Adventure kinds — OPTIONAL prefabs ---
    overview: kindDefSchema.parse({
        id: 'overview',
        label: 'Panoramica',
        icon: '📖',
        scope: 'adventure',
        singleton: true,
        storageDir: '',
        views: { list: 'page', detail: 'none' },
        fields: [f('body', 'markdown', 'Testo', { role: 'body' })],
    }),
    clue: kindDefSchema.parse({
        id: 'clue',
        label: 'Indizi',
        icon: '📜',
        scope: 'adventure',
        storageDir: 'clues',
        views: { list: 'journal', detail: 'profile' },
        fields: [
            f('title', 'text', 'Titolo', { role: 'title', required: true }),
            f('author', 'text', 'Autore', { role: 'subtitle' }),
            f('icon', 'text', 'Icona', { role: 'icon' }),
            f('image', 'image', 'Immagine di riferimento', { role: 'image' }),
            f('place', 'ref', 'Luogo', { target: 'place' }),
            f('text', 'markdown', 'Testo', { role: 'body' }),
        ],
    }),
    item: kindDefSchema.parse({
        id: 'item',
        label: 'Oggetti',
        icon: '🗝️',
        scope: 'adventure',
        storageDir: 'items',
        views: { list: 'grid', detail: 'profile' },
        fields: [
            f('name', 'text', 'Nome', { role: 'title', required: true }),
            f('icon', 'text', 'Icona', { role: 'icon' }),
            f('image', 'image', 'Immagine di riferimento', { role: 'image' }),
            f('type', 'text', 'Tipo', { role: 'subtitle' }),
            f('place', 'ref', 'Luogo', { target: 'place' }),
            f('description', 'markdown', 'Descrizione', { role: 'body' }),
        ],
    }),
    table: kindDefSchema.parse({
        id: 'table',
        label: 'Tabelle',
        icon: '🎲',
        scope: 'adventure',
        storageDir: 'tables',
        views: { list: 'table', detail: 'page' },
        fields: [
            f('name', 'text', 'Nome', { role: 'title' }),
            f('roll', 'text', 'Tiro', { role: 'subtitle' }),
            f('rule', 'markdown', 'Effetto', { role: 'body' }),
        ],
    }),
    page: kindDefSchema.parse({
        id: 'page',
        label: 'Pagina',
        icon: '📄',
        scope: 'adventure',
        singleton: true,
        storageDir: 'pages',
        views: { list: 'page', detail: 'none' },
        fields: [
            f('title', 'text', 'Titolo', { role: 'title' }),
            f('icon', 'text', 'Icona', { role: 'icon' }),
            f('markdown', 'markdown', 'Contenuto', { role: 'body' }),
        ],
    }),
};
export const BUILTIN_KIND_IDS = Object.keys(BUILTIN_KINDS);
// ---------------------------------------------------------------------------
// Back-compat: legacy SectionType -> kind id aliases
// ---------------------------------------------------------------------------
/**
 * Maps legacy `section.type` ids to the canonical kind id. Kind ids themselves are not keys
 * here, so applying this map is safe for both legacy types and already-canonical kinds.
 */
export const KIND_ALIASES = {
    rooms: 'place',
    characters: 'character',
    npcs: 'npc',
    clues: 'clue',
    items: 'item',
    bestiary: 'beast',
    encounters: 'encounter',
    curses: 'table',
    // overview, initiative, notes, gallery, page, custom are identity
};
/** Normalize any legacy kind/type id to its canonical built-in kind id. */
export function normalizeKindId(id) {
    return KIND_ALIASES[id] ?? id;
}
/**
 * Reverse map: canonical kind id -> legacy `section.type` for the (still type-based) runtime
 * renderer. Kinds with no legacy equivalent (music, compendium, custom) fall back to `custom`.
 */
export const KIND_TO_LEGACY_TYPE = {
    place: 'rooms',
    character: 'characters',
    npc: 'npcs',
    clue: 'clues',
    item: 'items',
    beast: 'bestiary',
    encounter: 'encounters',
    table: 'curses',
    overview: 'overview',
    initiative: 'initiative',
    notes: 'notes',
    gallery: 'gallery',
    page: 'page',
};
/** The legacy `section.type` value the current runtime renderer expects for a kind. */
export function legacyTypeForKind(kind) {
    return KIND_TO_LEGACY_TYPE[normalizeKindId(kind)] ?? 'custom';
}
// ---------------------------------------------------------------------------
// Resolver: merge built-in + custom kinds
// ---------------------------------------------------------------------------
/**
 * Merge the app's built-in kinds with an adventure's custom `kinds:` block into a single
 * lookup. Custom kinds may override built-ins (rare) and add new ones. The manifest stores
 * kinds keyed by id without repeating `id`; we fill it in from the key.
 */
export function resolveKinds(customKinds) {
    const out = { ...BUILTIN_KINDS };
    for (const [id, def] of Object.entries(customKinds ?? {})) {
        out[id] = kindDefSchema.parse({ ...def, id });
    }
    return out;
}
/** Look up a kind by id (built-in or custom), honoring legacy aliases. */
export function resolveKind(id, customKinds) {
    const all = resolveKinds(customKinds);
    return all[id] ?? all[normalizeKindId(id)];
}
//# sourceMappingURL=kinds.js.map